<script lang="ts">
  import { onMount } from 'svelte';
  import { debounce } from '$lib/helpers/browser/utils';
  import type { PandPCustomizationsProps } from './types';
  import layoutHelpers from '$lib/helpers/layout-helpers';

  export let subnavHeight: PandPCustomizationsProps['subnavHeight'];
  export let anchorLinkScrollAdjustMobile: PandPCustomizationsProps['anchorLinkScrollAdjustMobile'];
  export let anchorLinkScrollAdjustDesktop: PandPCustomizationsProps['anchorLinkScrollAdjustDesktop'];
  export let hideAnchorLinksOnTarget: PandPCustomizationsProps['hideAnchorLinksOnTarget'];
  export let hideSubnavCTAsFor: PandPCustomizationsProps['hideSubnavCTAsFor'];

  let desktopCtaSubnavCtaWrappers: NodeListOf<HTMLElement>;
  let mobileCtaSubnavFooter: HTMLDivElement;
  let observer: IntersectionObserver;
  let scrollAdjust: number;

  const anchorTargetSelectors: string[] = [];
  const anchorLinks: HTMLElement[] = [];

  const CLASS_NAMES = {
    VISIBLE: 'pw-next-visible',
    ANCHOR_LINK_ARROW: 'pw-next-anchor-link',
    FLIP_ARROW: 'pw-next-flip',
  };

  function showElement(element: any) {
    if (!element || element.classList.contains(CLASS_NAMES.VISIBLE)) {
      return;
    }

    element.classList.add(CLASS_NAMES.VISIBLE);
  }

  function hideElement(element: any) {
    if (!element || !element.classList.contains(CLASS_NAMES.VISIBLE)) {
      return;
    }

    element.classList.remove(CLASS_NAMES.VISIBLE);
  }

  function toggleSubnavCTAs(show: boolean) {
    desktopCtaSubnavCtaWrappers.forEach((cta) => {
      show ? showElement(cta) : hideElement(cta);
    });

    show ? showElement(mobileCtaSubnavFooter) : hideElement(mobileCtaSubnavFooter);
  }

  function pageCtaIntersectionObserverCallback(entries: any) {
    // check if any of the anchor targets are in view
    let isIntersectingAnchorTarget = false;
    entries.forEach((entry: any) => {
      isIntersectingAnchorTarget ||= entry.isIntersecting;
    });

    if (isIntersectingAnchorTarget) {
      // hide subnav CTAs if a page CTA is in view, or if an anchor target is in view with hideAnchorLinksOnTarget true
      toggleSubnavCTAs(false);
    } else {
      // otherwise, show subnav CTAs
      toggleSubnavCTAs(true);
    }
  }

  // override default anchor link behavior
  function onAnchorLinkClick(event: Event) {
    // prevent url from updating and jumping to target element
    event.preventDefault();
    // smooth scroll to target element
    const link = event.currentTarget as HTMLLinkElement;
    const targetId = link.getAttribute('href');

    if (targetId && targetId.indexOf('#') === 0) {
      const linkTarget = document.querySelector(targetId) as HTMLElement;
      const targetIsAbove = linkTarget?.getBoundingClientRect().top < scrollAdjust * -1;
      const finalScroll = targetIsAbove ? scrollAdjust - subnavHeight : scrollAdjust;
      const scrollPosition = linkTarget?.getBoundingClientRect().top + window.scrollY + finalScroll;
      window.scrollTo({ top: scrollPosition, behavior: 'smooth' });
    }
  }

  // add arrow icons and update click behavior of anchor links
  function updateAnchorLink(link: HTMLElement) {
    // override link click
    link.addEventListener('click', onAnchorLinkClick);

    // update arrow direction
    const linkSpan = link.querySelector('span');
    const linkText = linkSpan?.textContent;

    if (linkSpan && linkText?.endsWith('->')) {
      // remove arrow from string
      const newLinkText = linkText.slice(0, Math.max(0, linkText.length - 2));
      linkSpan.textContent = newLinkText;
      // add arrow class to link
      link.classList.add(CLASS_NAMES.ANCHOR_LINK_ARROW);
    }

    // save reference to subnav anchor link
    anchorLinks.push(link);
  }

  // rotates anchor link arrows to correctly point to their target elements
  function updateAnchorArrowDirection(anchorTarget: HTMLElement) {
    // get the links that anchor to the target
    const matchingLinks = anchorLinks.filter((link) => link.getAttribute('href') === `#${anchorTarget.id}`);
    // update the arrows for each link
    Array.from(matchingLinks)?.forEach((link) => {
      // check if the anchor target is above or below its linked-to scroll position
      if (anchorTarget.getBoundingClientRect().top < link.getBoundingClientRect().top) {
        // target is above the anchor link, point arrow up
        link?.classList.add(CLASS_NAMES.FLIP_ARROW);
      } else {
        // target is below the anchor link, point arrow down
        link?.classList.remove(CLASS_NAMES.FLIP_ARROW);
      }
    });
  }

  const onScroll = debounce(() => {
    // loop through anchor targets and update their matching link arrows
    anchorTargetSelectors.forEach((targetSelector) => {
      const anchorTarget = document.querySelector(targetSelector) as HTMLElement;
      if (anchorTarget) {
        updateAnchorArrowDirection(anchorTarget);
      }
    });
  }, 100);

  const onResize = debounce(() => {
    scrollAdjust =
      window.innerWidth < layoutHelpers.BREAKPOINT_WIDTHS.large.minWidth
        ? anchorLinkScrollAdjustMobile
        : anchorLinkScrollAdjustDesktop;
  }, 100);

  onMount(() => {
    onResize();

    // save refs to the subnav CTAs
    mobileCtaSubnavFooter = document.querySelector(
      '.MobileNav .cta-button-container, .CtaSubnavMobileNav .footer'
    ) as HTMLDivElement;
    desktopCtaSubnavCtaWrappers = document.querySelectorAll(
      '.DesktopNav .cta-button-container .cta-button, .CtaSubnavDesktopNav .cta-item'
    );

    // create an observer to watch anchor link targets
    observer = new IntersectionObserver(pageCtaIntersectionObserverCallback);

    // get the anchor links
    const anchorLinks = document.querySelectorAll('a');
    anchorLinks.forEach((anchor) => {
      const href: string | null = anchor.getAttribute('href');
      if (href && href.indexOf('#') === 0 && href.length > 1) {
        // check if the anchor link is in a subnav
        if (
          anchor.closest('.DesktopNav') ||
          anchor.closest('.MobileNav') ||
          anchor.closest('.CtaSubnavMobileNav') ||
          anchor.closest('.CtaSubnavDesktopNav')
        ) {
          // if so, observe its target element
          const target = document.querySelector(href);
          if (target && hideAnchorLinksOnTarget) {
            observer.observe(target);
          }
        }
        // save anchor link selectors
        anchorTargetSelectors.push(href);
        // update anchor link functionality
        updateAnchorLink(anchor as HTMLElement);
      }
    });

    // check for other elements on which to show/hide the subnav CTAs
    hideSubnavCTAsFor.forEach((selector) => {
      const element = document.querySelector(selector);
      if (element) {
        observer.observe(element);
      }
    });

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  });
</script>

<svelte:window on:scroll={onScroll} on:resize={onResize} />

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/tokens/generated-tokens/scss/_media_queries' as mediaQueries;

  @layer custom-components {
    @include mediaQueries.until('small') {
      :global {
        // hero button override
        .sq-custom-our-fees-hero {
          .TextStack__links .LinkComponent {
            flex: 1 1 max-content;

            .Action--button,
            .Action--button-alt {
              width: 100%;

              .Action__content-wrapper {
                display: block;
                margin: 0 auto;
                width: 100%;
              }
            }
          }
        }
      }
    }

    :global {
      .pw-next-anchor-link {
        span::after {
          content: url('https://images.ctfassets.net/2d5q1td6cyxq/6oItMw8t3Inxv2gEEP1ZpV/21f72b37870a68e191a56fe32a243367/arrow-up.svg');
          display: inline-block;
          position: relative;
          top: 0.1rem;
          margin-left: 0.5rem;
          transition: transform var(--duration-quick);
          transform: rotate(180deg); // start with the arrow pointing down
        }

        &.pw-next-flip {
          span::after {
            transform: rotate(0deg);
          }
        }
      }
    }
  }

  :global {
    .CtaSubnavDesktopNav,
    .DesktopNav {
      .right-slot .nav-list,
      .cta-subnav-list {
        justify-content: flex-end;
      }

      .cta-button-container {
        transition: max-width var(--duration-quick) var(--easing-decelerate);
      }

      .cta-button-container:not(:has(.pw-next-visible)) {
        max-width: 0;
      }

      .cta-button-container:has(.pw-next-visible) {
        max-width: 100%;
      }

      li.cta-item,
      li.cta-button {
        &,
        &.hidden {
          display: block !important;
          max-width: 0% !important;
          opacity: 0 !important;
          padding-left: 0 !important;
        }

        // turning Y animatin OFF since it's weird with subnav v2
        // transform: translateY(100%) !important;
        transition:
          transform calc(var(--duration-quick) / 2) var(--easing-sharp),
          opacity calc(var(--duration-quick) / 2) var(--easing-sharp),
          max-width var(--duration-quick) var(--easing-decelerate) !important;

        &.pw-next-visible {
          padding-left: 10px !important;
          max-width: 100% !important;
          opacity: 1 !important;
          transform: translateY(0%) !important;
          transition:
            max-width var(--duration-default) var(--easing-decelerate),
            transform var(--duration-quick) var(--easing-sharp),
            opacity var(--duration-quick) var(--easing-sharp) !important;
        }
      }
    }

    // Only remove padding left from subnav v2 under nav v2
    .PublicWebNavV2 {
      .CtaSubnavDesktopNav,
      .DesktopNav {
        .cta-subnav-container {
          li.cta-item,
          li.cta-button {
            &.pw-next-visible {
              padding-left: 0px !important;
            }
          }
        }
      }
    }

    // Transition values from public-web-nav
    .CtaSubnavMobileNav,
    .MobileNav {
      .footer,
      .cta-button-container {
        transform: translateY(100%) !important;
        transition: transform 75ms cubic-bezier(0.4, 0, 1, 1) !important;

        &.pw-next-visible {
          transform: translateY(0%) !important;
          transition: transform 150ms cubic-bezier(0, 0, 0.2, 1) !important;
        }
      }
      .pw-next-anchor-link {
        span::after {
          top: -0.1rem;
        }

        &.pw-next-flip {
          span::after {
            top: 0.1rem;
          }
        }
      }
    }
  }
</style>
